/**
 * 08.10.26 — справочник каналов: один подраздел за раз, панели из реестра.
 * Не удалять по требованию владельца системы.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button, TenantSelector } from '@/components/ui';
import { SubsectionSwitch } from '@/components/ui/SubsectionSwitch';
import { tenantsApi, type ChannelDirectoryData } from '@/api/tenants';
import { linearCanvas } from '@/features/linear-canvas/linearCanvas';
import {
  channelSectionIds,
  channelSectionRegistry,
  findChannelSection,
  type ChannelSectionId,
} from '@/features/channels-directory/channelSections';
import { useAuth } from '@/hooks/useAuth';
import { useSubsectionParam } from '@/hooks/useSubsectionParam';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/hooks/useToast';

export function ChannelsDirectoryPage() {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const queryClient = useQueryClient();
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const setSelectedTenant = useAuthStore((s) => s.setSelectedTenant);
  const [sectionId, setSectionId] = useSubsectionParam('section', channelSectionIds, 'telegram_client');

  const isSuperAdmin = !user?.tenant_id;
  const tenantId = isSuperAdmin ? selectedTenantId || undefined : user?.tenant_id;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['channels-directory', tenantId],
    queryFn: () => tenantsApi.getChannelDirectory(tenantId),
    enabled: !!tenantId,
  });

  const [form, setForm] = useState<ChannelDirectoryData | null>(null);

  useEffect(() => {
    if (data) {
      setForm(data);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!form) throw new Error('Нет данных для сохранения');
      return tenantsApi.updateChannelDirectory(
        {
          telegram_client_url: form.telegram_client_url || '',
          telegram_bot_username: form.telegram_bot_username || '',
          max_bot_username: form.max_bot_username || '',
          sms_sender_id: form.sms_sender_id || '',
        },
        tenantId,
      );
    },
    onSuccess: (updated) => {
      setForm(updated);
      queryClient.invalidateQueries({ queryKey: ['channels-directory', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-config', tenantId] });
      showSuccess('Информация по каналам сохранена');
    },
    onError: (error: unknown) => {
      const payload = error as { response?: { data?: { error?: string } }; message?: string };
      showError(payload.response?.data?.error || payload.message || 'Не удалось сохранить настройки каналов');
    },
  });

  const hasChanges = useMemo(() => {
    if (!data || !form) return false;
    return (
      (data.telegram_client_url || '') !== (form.telegram_client_url || '') ||
      (data.telegram_bot_username || '') !== (form.telegram_bot_username || '') ||
      (data.max_bot_username || '') !== (form.max_bot_username || '') ||
      (data.sms_sender_id || '') !== (form.sms_sender_id || '')
    );
  }, [data, form]);

  const section = findChannelSection(sectionId);
  const ActivePanel = section.Panel;

  const switchItems = channelSectionRegistry.map((item) => ({
    id: item.id,
    label: item.label,
    meta: form ? (item.configured(form) ? 'Настроен' : 'Не настроен') : undefined,
  }));

  return (
    <MainLayout
      headerTitle="Каналы уведомлений"
      disableMainScroll
      headerActions={
        isSuperAdmin && form ? (
          <>
            <Button
              variant="secondary"
              onClick={() => data && setForm(data)}
              disabled={!hasChanges || mutation.isPending}
            >
              Сбросить
            </Button>
            <Button
              onClick={() => mutation.mutate()}
              isLoading={mutation.isPending}
              disabled={!hasChanges}
              testId="btn-save-channels-directory"
            >
              <Save className="mr-2 h-4 w-4" />
              Сохранить
            </Button>
          </>
        ) : null
      }
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="shrink-0 space-y-3 border-b border-shell-border bg-shell-surface px-4 py-3">
          <p className="text-sm leading-5 text-shell-muted">{section.lead}</p>
          {isSuperAdmin && (
            <div className="max-w-xs">
              <TenantSelector
                value={selectedTenantId || ''}
                onChange={(id) => setSelectedTenant(id || null)}
                testId="select-tenant-channels-directory"
              />
            </div>
          )}
          <SubsectionSwitch
            items={switchItems}
            value={sectionId}
            onChange={(id) => setSectionId(id as ChannelSectionId)}
            testId="channels-directory-section"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {!tenantId && (
            <div className={linearCanvas.card}>Выберите клинику, чтобы открыть Telegram, MAX и SMS.</div>
          )}
          {tenantId && isLoading && <div className={linearCanvas.card}>Загрузка информации по каналам...</div>}
          {tenantId && isError && (
            <div className={linearCanvas.card}>Не удалось загрузить справочник каналов. Обновите страницу.</div>
          )}
          {form && (
            <div className="mx-auto max-w-3xl">
              <ActivePanel
                form={form}
                isSuperAdmin={isSuperAdmin}
                onChange={(patch) => setForm({ ...form, ...patch })}
              />
              {!isSuperAdmin && (
                <p className="mt-4 text-sm text-shell-muted">Для сотрудников клиники страница только для просмотра.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
